import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, merge } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { WebSocketService } from './websocket.service';
import { AuthService } from './auth.service';

export type ContractStatus = 'NEW' | 'PENDING' | 'SIGNED';
export type ContractType = 'BERTH_RENTAL' | 'BUOY_RENTAL' | 'SEASONAL' | 'ANNUAL' | 'MONTHLY' | 'OTHER';

export interface Contract {
  id?: string;  // UUID
  number: string;
  dateOfSigning?: string;  // ISO date
  startDate?: string;  // ISO date
  endDate?: string;  // ISO date
  status: ContractStatus;
  contractType?: ContractType;
  discountPercentage?: number;  // 0-100
  fixedMonthlyPrice?: number;
  fixedAnnualPrice?: number;
  currencyCode?: string;
  notes?: string;
  createdAt?: string;  // ISO datetime
  updatedAt?: string;  // ISO datetime
  
  // Related entity IDs
  legalEntityId?: string;
  customerId?: string;
  privatePersonId?: string;
  marinaId?: string;
  berthId?: string;
  buoyId?: string;
  vesselId?: string;
  clientId: string;
  
  // Denormalized names (from API response)
  legalEntityName?: string;
  customerName?: string;
  privatePersonName?: string;
  marinaName?: string;
  berthName?: string;
  buoyName?: string;
  vesselName?: string;
}

export interface CreateUpdateContract {
  number: string;
  dateOfSigning?: string;
  startDate?: string;
  endDate?: string;
  status: ContractStatus;
  contractType?: ContractType;
  discountPercentage?: number;
  fixedMonthlyPrice?: number;
  fixedAnnualPrice?: number;
  currencyCode?: string;
  notes?: string;
  legalEntityId?: string;
  customerId?: string;
  privatePersonId?: string;
  marinaId?: string;
  berthId?: string;
  buoyId?: string;
  vesselId?: string;
  clientId: string;
}

@Injectable({
  providedIn: 'root'
})
export class ContractService {
  private apiUrl = '/api/v1/contracts';

  constructor(
    private http: HttpClient,
    private router: Router,
    private webSocketService: WebSocketService,
    private authService: AuthService
  ) {
    console.log('[ContractService] Service initialized with WebSocket support');
  }
  
  /**
   * Get observable for real-time contract updates from WebSocket (create/update)
   */
  getRealtimeContractUpdates(): Observable<any> {
    const clientIds = this.authService.getClientIds();
    const observables = clientIds.map(clientId => 
      this.webSocketService.subscribe(`/topic/${clientId}.Contract`)
    );
    return merge(...observables);
  }
  
  /**
   * Get observable for real-time contract deletions from WebSocket
   */
  getRealtimeContractDeletions(): Observable<any> {
    const clientIds = this.authService.getClientIds();
    const observables = clientIds.map(clientId => 
      this.webSocketService.subscribe(`/topic/${clientId}.Contract`)
    );
    return merge(...observables);
  }

  /**
   * Get all contracts
   */
  getContracts(): Observable<Contract[]> {
    const headers = this.getAuthHeaders();
    
    console.log('Fetching contracts from:', this.apiUrl);

    return this.http.get<Contract[]>(this.apiUrl, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching contracts:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get contract by ID
   */
  getContractById(id: string): Observable<Contract> {
    const headers = this.getAuthHeaders();
    
    return this.http.get<Contract>(`${this.apiUrl}/${id}`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching contract by ID:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Contracts where this private person is a party (scoped to client).
   * @see OpenAPI GET /api/v1/contracts/private-person/{privatePersonId}/client/{clientId}
   */
  getContractsByPrivatePersonAndClient(privatePersonId: string, clientId: string): Observable<Contract[]> {
    const headers = this.getAuthHeaders();
    const url = `${this.apiUrl}/private-person/${encodeURIComponent(privatePersonId)}/client/${encodeURIComponent(clientId)}`;
    return this.http.get<Contract[]>(url, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching contracts for private person:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get contracts by status
   */
  getContractsByStatus(status: ContractStatus): Observable<Contract[]> {
    const headers = this.getAuthHeaders();
    
    return this.http.get<Contract[]>(`${this.apiUrl}/status/${status}`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching contracts by status:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Create a new contract
   */
  createContract(contract: CreateUpdateContract): Observable<Contract> {
    const headers = this.getAuthHeaders();
    
    console.log('Creating contract:', contract);

    return this.http.post<Contract>(this.apiUrl, contract, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error creating contract:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Update an existing contract
   */
  updateContract(id: string, contract: CreateUpdateContract): Observable<Contract> {
    const headers = this.getAuthHeaders();
    
    console.log('Updating contract:', id, contract);

    return this.http.put<Contract>(`${this.apiUrl}/${id}`, contract, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error updating contract:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Delete a contract
   */
  deleteContract(id: string): Observable<void> {
    const headers = this.getAuthHeaders();
    
    console.log('Deleting contract:', id);

    return this.http.delete<void>(`${this.apiUrl}/${id}`, { headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error deleting contract:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Download PDF for a contract
   */
  downloadContractPdf(id: string): Observable<Blob> {
    const headers = this.getAuthHeaders();
    
    // Remove Content-Type header for blob response
    let pdfHeaders = headers.delete('Content-Type');
    
    return this.http.get(`${this.apiUrl}/${id}/pdf`, { 
      headers: pdfHeaders,
      responseType: 'blob'
    }).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error downloading contract PDF:', error);
        this.handleAuthError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get authentication headers from localStorage
   */
  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('auth_token');
    const credentials = localStorage.getItem('auth_credentials');
    
    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    } else if (credentials) {
      headers = headers.set('Authorization', `Basic ${credentials}`);
    }

    return headers;
  }

  /**
   * Handle authentication errors
   */
  private handleAuthError(error: HttpErrorResponse): void {
    if (error.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_credentials');
      localStorage.removeItem('current_user');
      this.router.navigate(['/login']);
    }
  }
}

